'use client';

import { Raina } from '@magicschool/spellbook/components/Raina';
import { SHARP_ICON_NAMES, SharpIcon, type SharpIconName } from '@magicschool/spellbook/components/SharpIcon';
import { ImageGeneratorIcon } from '@magicschool/spellbook/components/ToolIcon/ImageGeneratorIcon';
import { StemGraphicIcon } from '@magicschool/spellbook/components/ToolIcon/StemGraphicIcon';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const toolIconVariants = cva(['inline-flex', 'items-center', 'justify-center', 'shrink-0'], {
  variants: {
    isOverride: {
      true: 'bg-transparent',
      false: '',
    },
    selected: {
      true: 'bg-transparent',
      false: '',
    },
    /** Default sharp-icon chip, or the inverted tokens for an opposite surface such as a tooltip. */
    surface: {
      default: '',
      inverted: '',
    },
    size: {
      sm: 'size-6 rounded-xs',
      md: 'size-8 rounded-sm',
      lg: 'size-12 rounded-sm',
      xl: 'size-15 rounded-md',
    },
  },
  compoundVariants: [
    { isOverride: false, selected: false, surface: 'default', className: 'bg-sharp-icon-background' },
    { isOverride: false, selected: false, surface: 'inverted', className: 'bg-sharp-icon-inverted-background' },
  ],
  defaultVariants: {
    size: 'md',
    isOverride: false,
    selected: false,
    surface: 'default',
  },
});

export type ToolIconProps = React.ComponentProps<'div'> &
  Pick<VariantProps<typeof toolIconVariants>, 'size' | 'selected' | 'surface'> & {
    /**
     * Icon name. Pass `"raina"` to render the Raina badge, `"image-generator"` to render the
     * image-generator SVG, or any SharpIcon name for a standard tool icon. Falls back to `"toolbox"`
     * for unrecognized values.
     */
    name: string | null;
    /** Accessible label for the icon. */
    label?: string;
    /**
     * Which Raina variant to render when `name === "raina"`. Defaults to `"head"` (the current
     * character). Pass `"legacy-badge"` to render the old hexagonal badge instead.
     */
    rainaVariant?: 'head' | 'legacy-badge';
  };

/**
 * Renders a tool's icon with its standard background treatment. Handles three name-driven
 * rendering paths: `"raina"` → Raina badge, `"image-generator"` → Adobe SVG,
 * any SharpIcon name → SharpIcon with rounded background (falls back to `"toolbox"`).
 *
 * ### When to use
 * - Inside `<ToolCard.Icon>` to display a tool's icon in a tool card
 * - Anywhere a tool's icon needs to appear with its standard rounded background
 *
 * ### When NOT to use
 * - For non-tool icons — use `SharpIcon` or `Icon` directly
 * - When you need a raw icon without background — use `SharpIcon` directly
 *
 * ### API
 * - `name`: `"raina"` | `"image-generator"` | SharpIcon name | `null` (falls back to `"toolbox"`)
 * - `size`: `sm` | `md` (default) | `lg` | `xl`
 * - `selected`: removes the background and switches icon fill tokens to selected-state colors
 * - `surface`: `default` or `inverted`. `inverted` uses the inverted sharp-icon tokens on the chip and the inner `SharpIcon`
 * - `label`: accessible label forwarded as `aria-label` on the inner icon element
 * - `rainaVariant`: which Raina variant to render for `name === "raina"` — `"head"` (default) or the old `"legacy-badge"`
 *
 * ### Visual states
 * - **Default**: rounded container with `bg-sharp-icon-background` and inner SharpIcon
 * - **selected**: transparent background, icon fill switches to selected-default tokens
 * - **raina / image-generator**: transparent background (no background pill), override SVG rendered at full container size
 */
export const ToolIcon = ({
  name,
  size = 'md',
  label,
  selected,
  surface = 'default',
  rainaVariant = 'head',
  className,
  ...props
}: ToolIconProps) => {
  const isRaina = name === 'raina';
  const isImageGenerator = name === 'image-generator';
  const isStemGraphic = name === 'stem-graphic';
  const isOverride = isRaina || isImageGenerator || isStemGraphic;

  return (
    <div data-spellbook-name="tool-icon" className={cn(toolIconVariants({ size, isOverride, selected, surface }), className)} {...props}>
      {isRaina ? (
        <Raina size="sm" variant={rainaVariant} aria-label={label} />
      ) : isImageGenerator ? (
        <ImageGeneratorIcon />
      ) : isStemGraphic ? (
        <StemGraphicIcon />
      ) : (
        <SharpIcon
          name={name != null && (SHARP_ICON_NAMES as readonly string[]).includes(name) ? (name as SharpIconName) : 'toolbox'}
          size={size ?? 'md'}
          aria-label={label}
          role={label ? 'img' : undefined}
          selected={selected ?? false}
          surface={surface ?? 'default'}
        />
      )}
    </div>
  );
};

ToolIcon.displayName = 'ToolIcon';
